import {
  Delete,
  ForwardToInbox,
  NoAccounts,
  SettingsBackupRestore,
  Edit,
} from '@mui/icons-material';
import { useState, useRef } from 'react';
import { useNotification } from '../../hooks/NotificationContext';
import {
  modifyUserAccountStatus,
  resendInvites,
  updateUserRole,
} from '../../modules/users';
import ActionMenu from '../../ui/primitives/ActionMenu';
import ConfirmationModal from '../../ui/primitives/ConfirmationModal';
import AddUserForm from './AddUserForm';

const UserActions = ({
  row,
  fetchCustomerList,
  currentUser,
}) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [loading, setLoading] = useState(false);
  const { addNotification } = useNotification();
  const formRef = useRef(null);

  const openConfirm = (action) => {
    setConfirmAction(action);
    setShowConfirm(true);
  };

  const closeConfirm = () => {
    setShowConfirm(false);
  };

  const handleSubmit = async () => {
    if (!confirmAction || !row?.id) return;

    if (confirmAction === 'edit') {
      formRef.current?.submit();
      return;
    }

    setLoading(true);
    try {
      if (confirmAction === 'invite') {
        await resendInvites(row);
        addNotification('success', 'Invite sent successfully!');
      } else {
        await modifyUserAccountStatus([row.id], confirmAction);
        const statusMsg = {
          delete: 'deleted permanently',
          deactivate: 'deactivated',
          restore: 'reactivated',
        };
        addNotification(
          'success',
          `User ${statusMsg[confirmAction]} successfully!`
        );
        fetchCustomerList();
      }
      closeConfirm();
    } catch (err) {
      addNotification('danger', err.message || 'Something went wrong.');
    }
    setLoading(false);
  };

  const handleEditSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        role: values.role,
        firstName: values.first_name,
        lastName: values.last_name,
        projects: values.projects,
      };

      await updateUserRole(payload, row.id);
      addNotification('success', 'User workspace profile updated successfully!');
      fetchCustomerList();
      closeConfirm();
    } catch (err) {
      addNotification('danger', err.message || 'Failed to update user workspace attributes.');
    } finally {
      setLoading(false);
    }
  };

  const isInviteDisabled = row?.status === 'invite-accepted';

  const confirmationTextMap = {
    invite: `Are you sure you want to resend the invite to ${row?.first_name}?`,
    delete: `Are you sure you want to permanently delete ${row?.first_name}? This action cannot be undone.`,
    deactivate: `Are you sure you want to deactivate ${row?.first_name}?`,
    restore: `Are you sure you want to reactivate ${row?.first_name}?`,
  };

  const menuItems = [
    {
      label: 'Edit User',
      icon: <Edit />,
      onClick: () => openConfirm('edit'),
      hidden: row?.status !== 'active',
    },
    {
      label: 'Resend Invite',
      icon: <ForwardToInbox />,
      onClick: () => openConfirm('invite'),
      disabled: isInviteDisabled,
      hidden: row?.status !== 'invited',
    },
    {
      label: 'Delete Permanently',
      icon: <Delete />,
      onClick: () => openConfirm('delete'),
      color: 'error',
      hidden: !(
        currentUser?.role === 'org-admin' &&
        row?.role !== 'org-admin' &&
        row?.status !== 'active'
      ),
    },
    {
      label: 'Deactivate User',
      icon: <NoAccounts />,
      onClick: () => openConfirm('deactivate'),
      color: 'warning',
      hidden: !(
        currentUser?.role === 'org-admin' &&
        row?.role !== 'org-admin' &&
        row?.status === 'active'
      ),
    },
    {
      label: 'Reactivate User',
      icon: <SettingsBackupRestore />,
      onClick: () => openConfirm('restore'),
      color: 'success',
      hidden: !(
        currentUser?.role === 'org-admin' &&
        row?.role !== 'org-admin' &&
        row?.status === 'inactive'
      ),
    },
  ];

  const modalTitleMap = {
    invite: 'Resend Invite',
    edit: 'Edit User Profile & Projects',
    delete: 'Delete User',
    deactivate: 'Deactivate User',
    restore: 'Reactivate User',
  };

  return (
    <>
      <ActionMenu items={menuItems} tooltip="User actions" />
      <ConfirmationModal
        show={showConfirm}
        handleClose={closeConfirm}
        handleConfirm={handleSubmit}
        title={modalTitleMap[confirmAction] ?? ''}
        body={
          confirmAction === 'edit' ? (
            <AddUserForm
              ref={formRef}
              handleSubmit={handleEditSubmit}
              isEditFlow
              initialData={row}
            />
          ) : (
            confirmationTextMap[confirmAction]
          )
        }
        loading={loading}
      />
    </>
  );
};

export default UserActions;